import React, { useContext, useRef, useState } from 'react';
import styled from 'styled-components';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import { FiBookmark } from 'react-icons/fi';
import { MdRefresh, MdOutlineFileUpload } from 'react-icons/md';
import { IconButton } from '@/features/editor/widgets/shared/ai-editing-layout/tab-container';
import { useAiComponentContext } from '@/features/editor/widgets/shared/ai-editing-layout/context';
import { EditorContext } from '@/features/editor/context/editor-context';
import { Spinner } from '@/Spinner';
// @ts-ignore
import widgetNoImage from './widget-no-image.png';
import { CompoundChange } from '@/pkg/sdk';

export function SaveWidgetPopover() {
  const {
    devicePreview: { runInIframe },
  } = useContext(EditorContext);

  const newChange = useAiComponentContext((ctx) => ctx.newChange);
  const bookmarkAiWidget = useAiComponentContext((ctx) => ctx.bookmarkAiWidget);

  const captureScreenshot = async () => {
    setIsLoading(true);

    try {
      runInIframe(async (_, win) => {
        try {
          const selector = `#${newChange.value.editorId}`;
          const imgData = await win[`takeElementScreenshot`](selector);

          if (!imgData) {
            setElemScreenshot(widgetNoImage);
            setIsLoading(false);
            return;
          }

          const CANVAS_WIDTH = 570;
          const CANVAS_HEIGHT = 764;

          const canvas = document.createElement(`canvas`);
          canvas.width = CANVAS_WIDTH;
          canvas.height = CANVAS_HEIGHT;
          const ctx = canvas.getContext(`2d`);

          if (ctx) {
            ctx.fillStyle = `white`;
            ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

            const img = new Image();
            img.onload = () => {
              const GAP = 20;
              const SKELETON_BG = `#f1f3f4`;
              const SKELETON_LINE = `#e2e5e9`;

              let displayWidth = img.width;
              let displayHeight = img.height;
              const maxWidth = CANVAS_WIDTH;
              const maxHeight = CANVAS_HEIGHT * 0.8;

              if (displayWidth > maxWidth) {
                const ratio = maxWidth / displayWidth;
                displayWidth = maxWidth;
                displayHeight *= ratio;
              }

              if (displayHeight > maxHeight) {
                const ratio = maxHeight / displayHeight;
                displayHeight = maxHeight;
                displayWidth *= ratio;
              }

              const x = (CANVAS_WIDTH - displayWidth) / 2;
              const y = (CANVAS_HEIGHT - displayHeight) / 2;

              ctx.drawImage(img, x, y, displayWidth, displayHeight);

              const drawSkeleton = (rectX, rectY, rectW, rectH) => {
                if (rectH <= 10) return;
                ctx.fillStyle = SKELETON_BG;
                ctx.fillRect(rectX, rectY, rectW, rectH);

                ctx.strokeStyle = SKELETON_LINE;
                ctx.lineCap = `round`;

                const centerX = rectX + rectW / 2;
                const centerY = rectY + rectH / 2;

                if (rectH > 30) {
                  ctx.lineWidth = 8;
                  ctx.beginPath();
                  ctx.moveTo(centerX - 100, centerY - 8);
                  ctx.lineTo(centerX + 100, centerY - 8);
                  ctx.stroke();

                  ctx.beginPath();
                  ctx.moveTo(centerX - 60, centerY + 8);
                  ctx.lineTo(centerX + 60, centerY + 8);
                  ctx.stroke();
                }
              };

              const topH = y - GAP;
              if (topH > 0) {
                drawSkeleton(0, 0, CANVAS_WIDTH, topH);
              }

              const bottomY = y + displayHeight + GAP;
              const bottomH = CANVAS_HEIGHT - bottomY;
              if (bottomH > 0) {
                drawSkeleton(0, bottomY, CANVAS_WIDTH, bottomH);
              }

              setElemScreenshot(canvas.toDataURL(`image/png`));
              setIsLoading(false);
            };

            img.onerror = () => {
              setElemScreenshot(widgetNoImage);
              setIsLoading(false);
            };

            img.src = imgData;
          } else {
            setElemScreenshot(widgetNoImage);
            setIsLoading(false);
          }
        } catch (e) {
          setElemScreenshot(widgetNoImage);
          setIsLoading(false);
        }
      });
    } catch (e) {
      setElemScreenshot(widgetNoImage);
      setIsLoading(false);
    }
  };

  const onBookmarkButtonClicked = async () => {
    setIsBookmarkOpen(true);
    captureScreenshot();
  };
  const [isBookmarkOpen, setIsBookmarkOpen] = useState(false);
  const [bookmarkName, setBookmarkName] = useState(newChange.value.hint);
  const [isLoading, setIsLoading] = useState(false);

  const [elemScreenshot, setElemScreenshot] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setElemScreenshot(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const onBookmark = async (id = ``) => {
    await bookmarkAiWidget(bookmarkName, elemScreenshot, id);
    setIsBookmarkOpen(false);
    setBookmarkName(``);
  };

  const isFromBookmark = (newChange.value.block.value as CompoundChange)
    ?.isFromBookmark;

  return (
    <Popover
      isOpen={isBookmarkOpen}
      positions={[`bottom`, `left`]}
      align="center"
      onClickOutside={() => setIsBookmarkOpen(false)}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor="white"
          arrowSize={10}
          arrowStyle={{
            top: `0.1rem`,
            zIndex: `9999`,
            filter: `drop-shadow(0px 1px 6px rgba(0, 0, 0, 0.1))`,
          }}
        >
          <BookmarkPopoverContent>
            <BookmarkTitle>
              Save this block as a reusable component
            </BookmarkTitle>
            <BookmarkInput
              type="text"
              placeholder="Block name"
              value={bookmarkName}
              onChange={(e) => setBookmarkName(e.target.value)}
              autoFocus
            />
            {isLoading && (
              <LoadingContainer>
                <LoadingText>Generating Screenshot, Please Wait...</LoadingText>
                <Spinner relative size="40" mt="0" />
              </LoadingContainer>
            )}
            {!isLoading && elemScreenshot && (
              <ImagePreviewContainer>
                <img alt="widget screenshot" src={elemScreenshot} />
                <ImageActions>
                  <RoundButton
                    onClick={(e) => {
                      e.stopPropagation();
                      captureScreenshot();
                    }}
                    title="Re-capture screenshot"
                  >
                    <MdRefresh size={16} />
                  </RoundButton>
                  <RoundButton
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                    title="Upload image"
                  >
                    <MdOutlineFileUpload size={16} />
                  </RoundButton>
                </ImageActions>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: `none` }}
                  accept="image/*"
                  onChange={onFileUpload}
                />
              </ImagePreviewContainer>
            )}
            {isFromBookmark && (
              <BookmarkSaveButton
                disabled={isLoading || !bookmarkName}
                onClick={() => onBookmark(isFromBookmark)}
              >
                Update Existing Component
              </BookmarkSaveButton>
            )}
            <BookmarkSaveButton
              disabled={isLoading || !bookmarkName}
              onClick={() => onBookmark(``)}
            >
              Save as New Component
            </BookmarkSaveButton>
          </BookmarkPopoverContent>
        </ArrowContainer>
      )}
    >
      <IconButton
        data-tip
        data-for="tt-save-to-ai-library"
        onClick={onBookmarkButtonClicked}
      >
        <FiBookmark
          size={22}
          color={isBookmarkOpen ? `#1f4eff` : `#535a61`}
          fill={isBookmarkOpen ? `#1f4eff` : `transparent`}
        />
      </IconButton>
    </Popover>
  );
}

export const BookmarkPopoverContent = styled.div`
  background: white;
  padding: 2.4rem;
  border-radius: 1.6rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  width: 32rem;
  border: 1px solid #f0f1f2;
`;

const BookmarkTitle = styled.h3`
  font-family: Inter, sans-serif;
  font-size: 1.6rem;
  font-weight: 600;
  color: #000;
  margin: 0;
  line-height: 1.4;
`;

const BookmarkInput = styled.input`
  width: 100%;
  padding: 1.2rem 1.6rem;
  border-radius: 0.8rem;
  border: 1px solid #d1d5db;
  font-family: Inter, sans-serif;
  font-size: 1.4rem;
  outline: none;

  &:focus {
    border-color: #1f4eff;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const BookmarkSaveButton = styled.button`
  width: 100%;
  padding: 1.2rem;
  border-radius: 0.8rem;
  background: #000;
  color: #fff;
  font-family: Inter, sans-serif;
  font-size: 1.4rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    background: #e2e5e9;
    color: #9ca3af;
    cursor: not-allowed;

    &:hover {
      opacity: 1;
    }

    &:active {
      transform: none;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.2rem;
  padding: 2rem 0;
  background: #f9fafb;
  border-radius: 0.8rem;
  border: 1px dashed #d1d5db;
`;

const LoadingText = styled.p`
  font-family: Inter, sans-serif;
  font-size: 1.2rem;
  font-weight: 500;
  color: #4b5563;
  margin: 0;
`;

const ImagePreviewContainer = styled.div`
  position: relative;
  width: 100%;
  border-radius: 0.8rem;
  overflow: hidden;

  img {
    width: 100%;
    display: block;
  }

  &:hover > div {
    opacity: 1;
  }
`;

const ImageActions = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.8rem;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
`;

const RoundButton = styled.button`
  width: 2.8rem;
  height: 2.8rem;
  border-radius: 50%;
  background: white;
  border: 1px solid #e2e5e9;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  color: #4b5563;
  transition: all 0.2s;

  &:hover {
    background: #f9fafb;
    color: #1f4eff;
    border-color: #1f4eff;
  }

  &:active {
    transform: scale(0.92);
  }
`;
